import { Auth } from "aws-amplify";
import moment from "moment";

import { appsyncError } from "../utils/errors";
import * as appsyncOperations from "./appsyncOperations";
import * as actions from "../store/actions";
import { setUsers } from "../store/features/users";
import { init as initServiceProvider } from "../store/features/serviceProvider";
import { setClients } from "../store/features/clients/slice";
import store from "../store";

const syncAll = async (serviceProviderId) => {
  if (!store.getState().app.globalLoading) {
    store.dispatch(actions.app.setGlobalLoading(true));

    let promiseArray = [];
    let responses = null;
    let {
      dars: darsTimestamp = moment().subtract(1, "days").valueOf(),
      divisions: divisionTimestamp = 0,
      divisionRelations: divisionRelationTimestamp = 0,
      properties: propertyTimestamp = 0,
      users: userTimestamp = 0,
      clients: clientsTimestamp = 0,
    } = store.getState().app.timestamps;

    try {
      const info = await Auth.currentCredentials();
      const cognitoIdentityId = info.identityId;
      await appsyncOperations.initClient(cognitoIdentityId);

      // Get all data we need and set up subscriptions
      promiseArray.push(
        appsyncOperations.getProperties({
          serviceProviderId,
          updatedAt: propertyTimestamp,
        })
      );
      promiseArray.push(
        appsyncOperations.getDivisions({
          serviceProviderId,
          from: divisionTimestamp,
        })
      );
      promiseArray.push(
        appsyncOperations.getDivisionRelations({
          serviceProviderId,
          from: divisionRelationTimestamp,
        })
      );
      promiseArray.push(
        appsyncOperations.getServiceProviderUsers({
          serviceProviderId,
          from: userTimestamp,
        })
      );
      promiseArray.push(
        darsTimestamp === 0
          ? appsyncOperations.queryPatrolStartTimesByServiceProviderId({
              serviceProviderId,
              from: darsTimestamp,
            })
          : appsyncOperations.queryPatrolsByServiceProviderIdUpdatedAtIndex({
              serviceProviderId,
              from: darsTimestamp,
            })
      );
      promiseArray.push(
        appsyncOperations.getServiceProvider(serviceProviderId)
      );
      promiseArray.push(
        appsyncOperations.queryClients({
          from: clientsTimestamp,
          serviceProviderId,
        })
      );

      // Wait for reply
      responses = await Promise.all(promiseArray);

      // Parse replies
      const data = {
        properties: responses[0],
        divisions: responses[1],
        divisionRelations: responses[2],
        users: responses[3],
        patrolStartTimes: responses[4],
        serviceProvider: responses[5],
        clients: responses[6],
      };

      store.dispatch(actions.properties.setProperties(data.properties));
      store.dispatch(
        actions.divisions.setDivisions({
          divisions: data.divisions,
          divisionRelations: data.divisionRelations,
        })
      );
      store.dispatch(setUsers({ users: data.users }));
      store.dispatch(actions.dars.setDars(data.patrolStartTimes));
      store.dispatch(
        initServiceProvider({ serviceProvider: data.serviceProvider })
      );
      store.dispatch(setClients({ clients: data.clients }));

      promiseArray = [];

      for (const property of data.properties) {
        if (property.updatedAt > propertyTimestamp) {
          propertyTimestamp = property.updatedAt;
        }
        if (!property.isDeleted && property.instanceId) {
          promiseArray.push(
            appsyncOperations.getPropertyEventsForInstance(property.instanceId)
          );
        }
      }
      for (const division of data.divisions) {
        if (division.updatedAt > divisionTimestamp) {
          divisionTimestamp = division.updatedAt;
        }
      }
      for (const divisionRelation of data.divisionRelations) {
        if (divisionRelation.updatedAt > divisionRelationTimestamp) {
          divisionRelationTimestamp = divisionRelation.updatedAt;
        }
      }
      for (const user of data.users) {
        if (user.updatedAt > userTimestamp) {
          userTimestamp = user.updatedAt;
        }
      }
      for (const patrol of data.patrolStartTimes) {
        if (patrol.updatedAt > darsTimestamp) {
          darsTimestamp = patrol.updatedAt;
        }
      }
      store.dispatch(
        actions.app.setResyncTimestamp({
          darsTimestamp,
          divisionTimestamp,
          divisionRelationTimestamp,
          propertyTimestamp,
          userTimestamp,
        })
      );

      responses = await Promise.all(promiseArray);

      for (let index = 0; index < responses.length; index++) {
        const propertyEvents = responses[index];
        let lastVisited = null;
        if (propertyEvents.length > 0) {
          const { instanceId } = propertyEvents[0];
          const lastVisitedString = localStorage.getItem(instanceId);
          lastVisited =
            lastVisitedString !== null
              ? Number.parseInt(lastVisitedString)
              : null;
        }
        store.dispatch(
          actions.properties.setPropertyEvents({ propertyEvents, lastVisited })
        );
      }

      store.dispatch(actions.app.setGlobalLoading(false));
    } catch (e) {
      console.error(e);
      store.dispatch(actions.app.setGlobalError(appsyncError(e)));
      throw e;
    }
  }
};

let lockout = false;

const syncAllWithRetries = async (serviceProviderId) => {
  if (lockout || !serviceProviderId) return;
  else lockout = true;

  const maxRetries = 10;

  for (let retries = 0; retries < maxRetries; retries++) {
    try {
      await syncAll(serviceProviderId);
      break;
    } catch (e) {
      continue;
    }
  }

  lockout = false;
};

export default syncAllWithRetries;
